import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { styled, keyframes, vh, darkTheme } from "~/ui/style/stitches.config";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";

import { createPost, getTarotCards, TarotCard, getPostCooldownStatus } from "~/api/communityApi";
import { getJournalEntries, JournalEntry } from "~/api/journalApi";
import { TarotReading } from "~/types/summaries";
import { Button } from "~/ui/components/Button";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import { trackEvent } from "~/api/analyticsApi";
import { motion } from "framer-motion";
import { sendToastMessageWithFallbackCode } from "~/utils/handleToasts";
import { imageUrl } from "~/utils/imageurl";
import { expandSearchTerms } from "~/utils/searchUtils";
import { isIOSNativeApp } from "~/utils/deviceDetection";

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const getDeckSlugFromPath = (imagePath: string): string => {
  if (!imagePath?.includes('/decks/')) return 'rider-waite';
  const folderName = imagePath.split('/decks/')[1]?.split('/')[0];
  return folderName || 'rider-waite';
};

// Card data that can be passed from the room Summary
export interface InitialCardData {
  name: string;
  imagePath: string;
  position: string;
  isReversed: boolean;
}

interface CreatePostOverlayProps {
  onClose: () => void;
  initialQuestion?: string;
  initialCards?: InitialCardData[];
}

interface SelectedCard {
  cardId: string;
  name: string;
  imagePath: string;
  position: string;
  isReversed: boolean;
}

export const CreatePostOverlay = ({
  onClose,
  initialQuestion,
  initialCards
}: CreatePostOverlayProps) => {
  const [, setLocation] = useLocation();
  const { appDarkMode: dk } = useSnapshot(localState);

  const [cards, setCards] = useState<TarotCard[]>([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [selectedCards, setSelectedCards] = useState<SelectedCard[]>([]);
  const [question, setQuestion] = useState(initialQuestion || "");
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSuit, setSelectedSuit] = useState<string | null>("major");
  const [loadedSuits, setLoadedSuits] = useState<Set<string>>(new Set());
  const [editingPositionIndex, setEditingPositionIndex] = useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Post cooldown state
  const [cooldownHours, setCooldownHours] = useState<number | null>(null);
  const [checkingCooldown, setCheckingCooldown] = useState(true);

  const overlayRef = useRef<HTMLDivElement>(null);

  // Journal import state
  const [showJournalPicker, setShowJournalPicker] = useState(false);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [loadingJournal, setLoadingJournal] = useState(false);
  const [deckName, setDeckName] = useState("rider-waite");

  // Check post cooldown on mount
  useEffect(() => {
    const checkCooldown = async () => {
      try {
        const status = await getPostCooldownStatus();
        if (!status.canPost) {
          setCooldownHours(status.hoursRemaining);
        }
      } catch (err) {
        console.error("Failed to check post cooldown:", err);
      } finally {
        setCheckingCooldown(false);
      }
    };
    checkCooldown();
  }, []);

  // Pre-populate selected cards from initial data (from room summary)
  useEffect(() => {
    if (initialCards && initialCards.length > 0) {
      const prePopulatedCards: SelectedCard[] = initialCards.map((card, index) => ({
        cardId: `initial-${index}`, // Temporary ID for pre-populated cards
        name: card.name,
        imagePath: card.imagePath,
        position: card.position,
        isReversed: card.isReversed,
      }));
      setSelectedCards(prePopulatedCards);
      const firstPath = initialCards[0]?.imagePath;
      if (firstPath) setDeckName(getDeckSlugFromPath(firstPath));
    }
  }, [initialCards]);

  // Reset confirmation if user adds/removes cards
  useEffect(() => {
    if (showConfirm) {
      setShowConfirm(false);
    }
  }, [selectedCards.length]);

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const data = await getTarotCards();
        setCards(data);

        // Preload only Major Arcana images initially
        const majorCards = data.filter(c => c.category === "major");
        await Promise.all(
          majorCards.map(card => {
            if (!card.imagePath) return Promise.resolve();
            return new Promise<void>((resolve) => {
              const img = new Image();
              img.onload = () => resolve();
              img.onerror = () => resolve();
              img.src = imageUrl(card.imagePath!, 100);
            });
          })
        );
        setLoadedSuits(new Set(["major"]));
      } catch (err) {
        console.error("Failed to fetch cards:", err);
      } finally {
        setLoadingCards(false);
      }
    };
    fetchCards();
  }, []);

  const filteredCards = cards.filter(card => {
    const cardNameLower = card.name.toLowerCase();
    const expandedTerms = expandSearchTerms(searchTerm);
    return expandedTerms.some(term => cardNameLower.includes(term));
  });

  const groupedCards = {
    major: filteredCards.filter(c => c.category === "major"),
    wands: filteredCards.filter(c => c.category === "wands"),
    cups: filteredCards.filter(c => c.category === "cups"),
    swords: filteredCards.filter(c => c.category === "swords"),
    pentacles: filteredCards.filter(c => c.category === "pentacles"),
  };

  // Preload images for a suit when selected
  const handleSuitClick = async (suit: string) => {
    if (selectedSuit === suit) return; // Already selected

    setSelectedSuit(suit);

    // If already loaded, nothing more to do
    if (loadedSuits.has(suit)) return;

    // Preload images for this suit
    const suitCards = cards.filter(c => c.category === suit);
    await Promise.all(
      suitCards.map(card => {
        if (!card.imagePath) return Promise.resolve();
        return new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = imageUrl(card.imagePath!, 100);
        });
      })
    );
    setLoadedSuits(prev => new Set(prev).add(suit));
  };

  const handleOpenJournalPicker = async () => {
    setShowJournalPicker(true);
    setLoadingJournal(true);
    try {
      const entries = await getJournalEntries(20);
      // Only show entries that have cards
      setJournalEntries(entries.filter(e => e.content?.cards?.length > 0));
    } catch (err) {
      console.error("Failed to fetch journal entries:", err);
    } finally {
      setLoadingJournal(false);
    }
  };

  const handleImportJournalEntry = (entry: JournalEntry) => {
    trackEvent("/community/imported_journal_entry");
    const content = entry.content;

    // Map journal cards to selected cards
    const imported: SelectedCard[] = (content.cards || []).slice(0, 10).map((card: any, index: number) => {
      const isReversed = card.name?.includes('Reversed') || card.name?.includes('(Reversed)');
      const cleanName = card.name?.replace(/ \(Reversed\)| Reversed/g, '').trim() || '';
      // Journal imagePaths are full URLs; strip to relative path so imageUrl() works
      let path = card.imagePath || "";
      if (path.includes("imgix.net")) {
        try {
          const url = new URL(path);
          path = url.pathname;
        } catch {}
      }
      return {
        cardId: `journal-${index}`,
        name: cleanName,
        imagePath: path,
        position: card.position || "",
        isReversed: !!isReversed,
      };
    });

    setSelectedCards(imported);
    setQuestion(content.query || "");
    setShowJournalPicker(false);
    const firstPath = imported[0]?.imagePath;
    if (firstPath) setDeckName(getDeckSlugFromPath(firstPath));
    // Delay scroll to allow DOM to update after state changes
    setTimeout(() => {
      overlayRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const addCard = (card: TarotCard) => {
    if (selectedCards.length >= 10) return;
    setSelectedCards([...selectedCards, {
      cardId: card.id,
      name: card.name,
      imagePath: card.imagePath || "",
      position: "",
      isReversed: false,
    }]);
    setSearchTerm("");
  };

  const removeCard = (index: number) => {
    setSelectedCards(selectedCards.filter((_, i) => i !== index));
  };

  const updateCard = (index: number, updates: Partial<SelectedCard>) => {
    setSelectedCards(selectedCards.map((card, i) => 
      i === index ? { ...card, ...updates } : card
    ));
  };

  const handlePostClick = () => {
    if (selectedCards.length === 0) {
      sendToastMessageWithFallbackCode("Please add at least one card", true, 0);
      return;
    }
    setShowConfirm(true);
  };

  const handleSubmit = async () => {
    const finalQuestion = question.trim() || "Interpret this:";

    try {
      setSubmitting(true);

      const reading: TarotReading = {
        cards: selectedCards.map(card => ({
          name: card.isReversed ? `${card.name} (Reversed)` : card.name,
          position: card.position || null,
          interpretation: "",
          imagePath: card.imagePath ? imageUrl(card.imagePath, 400) : "",
        })),
        query: finalQuestion,
        overall: "",
        question: finalQuestion,
        gist: "",
        tarotTip: "",
        hint: "",
      };

      const newPost = await createPost({
        question: finalQuestion,
        reading,
        deckName,
      });

      trackEvent("/community/created_post");
      onClose();
      setLocation(`/post/${newPost.id}`);
    } catch (err: any) {
      sendToastMessageWithFallbackCode(err.response?.data?.error || "Failed to create post", true, 0);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <StyledOverlay
      as={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <StyledOverlayContent
        ref={overlayRef}
        as={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        isIOSNative={isIOSNativeApp()}
      >
        <div className="sticky-header">
          {showJournalPicker && (
            <img
              className="back-button"
              onClick={() => setShowJournalPicker(false)}
              src="/images/back-button.svg"
              alt="Back"
            />
          )}
          <img
            className="x-button"
            onClick={onClose}
            src="/images/exit.svg"
            style={{
              width: '28px',
              height: '28px',
              cursor: 'pointer',
              opacity: 1,
              transition: 'opacity 0.2s ease',
              userSelect: 'none',
            }}
            onMouseOver={e => e.currentTarget.style.opacity = '0.6'}
            onMouseOut={e => e.currentTarget.style.opacity = '1'}
          />
        </div>

        <div className="overlay-body">
          {/* Import from journal button - show when no cards selected yet */}
          {selectedCards.length === 0 && !showJournalPicker && (
            <button
              className="import-journal-button"
              onClick={handleOpenJournalPicker}
            >
              Import from journal
            </button>
          )}

          {/* Question input */}
          <div style={{ marginBottom: '30px', width: '100%', display: showJournalPicker ? 'none' : undefined }}>
            <h2 className="question-heading" style={{
              marginBottom: '12px',
              fontSize: '25px',
              fontFamily: 'KeplerLightCondensed',
              marginTop: '.5rem',
              fontWeight: 100,
            }}>Your question</h2>
            <textarea
              className="question-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="I'd like clarity on..."
              maxLength={300}
              style={{
                width: '100%',
                minHeight: '60px',
                backgroundColor: dk ? '#000' : '#fff',
                border: dk ? '1px solid #505050' : '1px solid #ddd',
                borderRadius: '12px',
                padding: '12px',
                color: dk ? '#fff' : '#000',
                fontSize: '12px',
                lineHeight: '1.4',
                resize: 'none',
                outline: 'none',
                transition: 'border-color 0.2s ease',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = dk ? '#aaa' : '#888888'}
              onBlur={(e) => e.target.style.borderColor = dk ? '#505050' : '#ddd'}
            />
            <p style={{
              fontSize: '10px',
              color: '#999',
              marginTop: '6px',
              marginLeft: '4px',
            }}>No real names. Initials or nicknames work great!</p>
          </div>

          {/* Journal picker */}
          {showJournalPicker && (
            <div className="journal-picker">
              <h2 style={{
                fontSize: '15px',
                fontWeight: 400,
                margin: '20px 0 6px 0',
              }}>Choose a reading.</h2>
              <p style={{
                fontSize: '11px',
                color: dk ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
                lineHeight: '1.3',
                margin: '2px 0 18px 0',
              }}>You can edit before posting. Reflections won't be shared.</p>

              {loadingJournal ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '30px', minHeight: '200px' }}>
                  <SimpleLoadingIndicator style={{ position: 'static', width: '20px', height: '20px' }} />
                </div>
              ) : journalEntries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', fontSize: '13px', color: '#888' }}>
                  No journal entries yet. Open the summary feature in a tarot room to save your readings.
                </div>
              ) : (
                <motion.div
                  className="journal-entries-list"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {journalEntries.map((entry) => {
                    const entryDate = new Date(entry.createdAt);
                    const dateStr = entryDate.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    });
                    const cards = entry.content?.cards || [];
                    return (
                      <button
                        key={entry.id}
                        className="journal-entry-option"
                        onClick={() => handleImportJournalEntry(entry)}
                      >
                        <div className="journal-entry-cards">
                          {cards.slice(0, 5).map((card: any, i: number) => (
                            <img
                              key={i}
                              src={card.imagePath?.replace(/w=\d+|w=undefined/, 'w=80') || ''}
                              alt={card.name}
                              style={{
                                transform: (card.name?.includes('Reversed') || card.name?.includes('(Reversed)'))
                                  ? 'rotate(180deg)' : 'none'
                              }}
                            />
                          ))}
                          {cards.length > 5 && (
                            <span className="journal-entry-more">+{cards.length - 5}</span>
                          )}
                        </div>
                        <div className="journal-entry-info">
                          <span className="journal-entry-query">
                            {entry.content?.query || 'Untitled reading'}
                          </span>
                          <span className="journal-entry-date">{dateStr}</span>
                        </div>
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </div>
          )}

          {/* Cards section */}
          <div className="form-section" style={{ display: showJournalPicker ? 'none' : undefined }}>
            <h2 style={{
              marginBottom: '12px',
              fontSize: '11px',
              textTransform: 'uppercase',
              marginTop: '.5rem',
              fontWeight: 400,
            }}>Cards ({selectedCards.length}/10)</h2>

            {/* Selected cards */}
            {selectedCards.length > 0 && (
              <>
              <div className="selected-cards">
                {selectedCards.map((card, index) => (
                  <div key={index} className="selected-card">
                    <div className="card-row">
                      <div className="card-preview">
                        {card.imagePath && (
                          <img
                            src={imageUrl(card.imagePath, 100)}
                            alt={card.name}
                            style={{ transform: card.isReversed ? 'rotate(180deg)' : 'none' }}
                          />
                        )}
                        <button className="remove-card" onClick={() => removeCard(index)}>×</button>
                      </div>
                      <button
                        className={`reverse-button ${card.isReversed ? 'active' : ''}`}
                        onClick={() => updateCard(index, { isReversed: !card.isReversed })}
                        title={card.isReversed ? 'Set upright' : 'Set reversed'}
                      >
                        <img
                          src="/images/room/reverse-tool.svg"
                          alt="Reverse"
                          style={{ transform: card.isReversed ? 'scaleY(1)' : 'scaleY(-1)' }}
                        />
                      </button>
                    </div>
                    <div className="card-name">{card.name}{card.isReversed ? ' (R)' : ''}</div>
                    {editingPositionIndex === index || !card.position ? (
                      <input
                        type="text"
                        className="position-input"
                        value={card.position}
                        onChange={(e) => updateCard(index, { position: e.target.value })}
                        placeholder="Slot name"
                        maxLength={50}
                        autoFocus={editingPositionIndex === index}
                        onFocus={() => setEditingPositionIndex(index)}
                        onBlur={() => setEditingPositionIndex(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                      />
                    ) : (
                      <div
                        className="position-display"
                        onClick={() => setEditingPositionIndex(index)}
                      >
                        {card.position}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {!selectedCards.some(card => card.position.trim()) && (
                <p style={{
                  fontSize: '10px',
                  color: '#999',
                  marginTop: '-10px',
                  marginLeft: '6px',
                  marginBottom: '16px',
                  lineHeight: '1.4',
                }}>No numbers needed. Spread slots (Hopes, Fears, etc.) help with interpretations.</p>
              )}
              </>
            )}

            {/* Search input - below selected cards */}
            {selectedCards.length < 10 && (
              <div className="search-container">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  placeholder="Search for a card..."
                  className="card-search"
                />
                <div
                  className={`clear-icon ${searchTerm ? "visible" : ""}`}
                  onClick={() => setSearchTerm("")}
                />
              </div>
            )}

            {/* Card picker */}
            {selectedCards.length < 10 && (
              <>
              {loadingCards ? (
                <div className="loading-cards">
                  <SimpleLoadingIndicator style={{ position: 'static', width: '20px', height: '20px' }} />
                </div>
              ) : (
                <div className="card-picker">
                  {/* Suit filter buttons - always visible */}
                  <div className="suit-buttons">
                    {["major", "wands", "cups", "swords", "pentacles"].map(suit => (
                      <button
                        key={suit}
                        className={`suit-button ${selectedSuit === suit ? "active" : ""}`}
                        onClick={() => handleSuitClick(suit)}
                      >
                        {suit === "major" ? "Major" : suit === "pentacles" ? "Pents" : suit.charAt(0).toUpperCase() + suit.slice(1)}
                      </button>
                    ))}
                  </div>

                  {/* Card grid */}
                  <motion.div
                    key={selectedSuit || "all"}
                    className={`category-cards ${selectedSuit && loadedSuits.has(selectedSuit) ? "loaded" : ""}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.15 }}
                  >
                    {(searchTerm
                      ? filteredCards
                      : selectedSuit
                        ? groupedCards[selectedSuit as keyof typeof groupedCards] || []
                        : []
                    ).map(card => (
                      <button
                        key={card.id}
                        className="card-option"
                        onClick={() => addCard(card)}
                        disabled={selectedCards.some(sc => sc.cardId === card.id)}
                      >
                        {card.imagePath && (
                          <img
                            src={imageUrl(card.imagePath, 100)}
                            alt={card.name}
                          />
                        )}
                        <span>{card.name}</span>
                      </button>
                    ))}
                  </motion.div>
                </div>
              )}
              </>
            )}
          </div>

        </div>

        <div className="overlay-footer" style={{ display: showJournalPicker ? 'none' : undefined }}>
          {checkingCooldown ? null : cooldownHours !== null ? (
            <div
              key="cooldown"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                fontSize: '13px',
                color: dk ? '#fff' : '#000',
                lineHeight: '1.4',
                padding: '12px 20px',
                opacity: 0,
                animation: `${fadeIn} 0.5s ease forwards`,
                animationDelay: '0.12s',
              }}
            >
              <div>You can only post once per day.</div>
              <div style={{ fontSize: '12px', color: dk ? 'rgba(255, 255, 255, 0.6)' : 'rgb(0, 0, 0, .5)', width: '90%' }}>This helps everyone get a chance at a thoughtful reply. Come back in <span style={{ fontWeight: 500, color: dk ? '#fff' : '#000' }}>{cooldownHours}</span> hour{cooldownHours === 1 ? '' : 's'}!</div>
            </div>
          ) : showConfirm ? (
            <div
              key="confirm"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                opacity: 0,
                animation: `${fadeIn} 0.5s ease forwards`,
                animationDelay: '0.12s',
                position: 'relative',
              }}
            >
              <Button
                Theme={dk ? "dark" : "light"}
                className="arrow-button"
                role="menuitem"
                style={{
                  position: 'absolute',
                  left: '-144px',
                  top: '16px',
                  transform: 'translateY(-50%)',
                }}
                css={{
                  '@mobile': {
                    left: '-96px !important',
                    top: '-1px !important',
                    transform: 'none !important',
                  },
                }}
                transparent
                onClick={() => setShowConfirm(false)}
              >
                &larr;
              </Button>
              <div style={{
                textAlign: 'center',
                fontSize: '12px',
                opacity: 1,
                maxWidth: '300px',
                lineHeight: '1.4',
                marginTop: '8px',
                height: 'auto',
                transition: 'all 0.2s ease-in-out',
              }}>
                Ready to share?
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                marginTop: '-1px',
                alignItems: 'center',
                height: 'auto',
                transition: 'all 0.2s ease-in-out',
                paddingBottom: '10px',
              }}>
                <Button
                  Theme="light"
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    padding: '10px 22px',
                    borderRadius: '20px',
                    border: '0px solid',
                    backgroundColor: dk ? '#fff' : 'var(--colors-grayA100)',
                    fontSize: '12px',
                    width: '110px',
                    minHeight: '34px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  css={{
                    '&:hover': {
                      backgroundColor: dk ? '#e5e5e5 !important' : '$gray200 !important',
                    },
                  }}
                >
                  {submitting ? (
                    <SimpleLoadingIndicator style={{
                      width: "14px",
                      height: "14px",
                      position: "relative",
                      top: "0px",
                      opacity: "0.6"
                    }} />
                  ) : "Continue"}
                </Button>
              </div>
            </div>
          ) : (
            <div
              key="post"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                opacity: 0,
                animation: `${fadeIn} 0.5s ease forwards`,
                animationDelay: '0.12s',
              }}
            >
              <Button
                Theme={dk ? "dark" : "light"}
                onClick={onClose}
                style={{
                  padding: '10px 20px',
                  border: dk ? '1px solid rgba(255,255,255,0.6)' : '1px solid #000',
                  borderRadius: '12px',
                  fontSize: '12px',
                  minWidth: '90px',
                }}
              >
                Cancel
              </Button>
              <Button
                Theme={dk ? "light" : "dark"}
                onClick={handlePostClick}
                style={{
                  padding: '10px 20px',
                  border: dk ? '1px solid rgba(255,255,255,0.6)' : '1px solid #000',
                  borderRadius: '12px',
                  fontSize: '12px',
                  minWidth: '90px',
                }}
              >
                Post
              </Button>
            </div>
          )}
        </div>
      </StyledOverlayContent>
    </StyledOverlay>,
    document.body
  );
};

const StyledOverlay = styled("div", {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(180, 180, 180, 0.4)",
  [`.${darkTheme.className} &`]: { backgroundColor: "rgba(0, 0, 0, 0.6)" },
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 999999,

  "@mobile": {
    backgroundColor: "transparent !important",
    height: "-webkit-fill-available",
  },
});

const StyledOverlayContent = styled("div", {
  backgroundColor: "$wash",
  borderRadius: "24px",
  border: "1px solid $gray600",
  padding: "32px",
  maxWidth: "460px",
  width: "90%",
  maxHeight: vh(84),
  minHeight: vh(50),
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  overflowY: "scroll",
  overflowX: "hidden",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: "6px",
    backgroundColor: "rgba(127, 127, 127, .5)",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
    marginTop: "24px",
    marginBottom: "24px",
  },
  "&::-webkit-scrollbar-corner": {
    backgroundColor: "transparent",
  },

  "& .sticky-header": {
    position: "sticky",
    top: "0px",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "0px 10px 30px 10px",
    zIndex: 4,
    width: "calc(100% + 64px)",
    marginLeft: "-32px",
    marginRight: "-32px",
    marginTop: "-32px",
    boxSizing: "border-box",
    borderTopLeftRadius: "24px",
    borderTopRightRadius: "24px",
  },

  "& .back-button": {
    width: "28px",
    height: "28px",
    cursor: "pointer",
    opacity: 0.8,
    marginRight: "auto",
    marginLeft: "4px",
    transition: "opacity 0.2s ease",
    "&:hover": {
      opacity: 0.5,
    },
  },

  "@mobile": {
    border: "none !important",
    borderRadius: 0,
    width: "100%",
    height: "100%",
    maxWidth: "none",
    maxHeight: "none !important",
    minHeight: `calc(${vh(100)} - 140px)`,
    margin: 0,
    paddingTop: "24px !important",

    "& .sticky-header": {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
  },

  "& .overlay-body": {
    flex: 1,
    width: "100%",
    paddingBottom: "100px",
    "@mobile": {
      paddingBottom: "120px",
    },
  },

  "& .question-heading": {
    "@mobile": {
      fontSize: "26px !important",
    },
  },

  "& .question-input": {
    "@mobile": {
      fontSize: "13px !important",
    },
  },

  "& .form-section": {
    marginBottom: "20px",

    "& label": {
      display: "block",
      fontSize: "12px",
      fontWeight: "500",
      marginBottom: "8px",
      color: "#333",
    },

    "& textarea": {
      width: "100%",
      padding: "12px",
      border: "1px solid #ddd",
      borderRadius: "10px",
      fontSize: "13px",
      fontFamily: "inherit",
      resize: "none",

      "&:focus": {
        outline: "none",
        borderColor: "#000",
      },
    },
  },

  "& .selected-cards": {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px 16px",
    width: "100%",
    marginBottom: "16px",
  },

  "& .selected-card": {
    textAlign: "center",

    "& .card-row": {
      position: "relative",
      display: "flex",
      justifyContent: "center",
      marginBottom: "4px",
    },

    "& .card-preview": {
      position: "relative",

      "& img": {
        width: "60px",
        height: "auto",
        borderRadius: "4px",
        border: "1px solid #ddd",
        userSelect: "none",
      },

      "& .remove-card": {
        position: "absolute",
        top: "-6px",
        right: "-6px",
        width: "18px",
        height: "18px",
        borderRadius: "50%",
        backgroundColor: "#000",
        color: "#e5e5e5",
        border: "none",
        fontSize: "13px",
        fontWeight: "200",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },

    "& .reverse-button": {
      position: "absolute",
      right: "-2px",
      top: "50%",
      transform: "translateY(-50%)",
      background: "transparent",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      padding: "4px",
      opacity: 0.4,
      transition: "opacity 0.2s ease, background 0.2s ease",

      "@mobile": {
        right: "-8px",
      },

      "&:hover": {
        opacity: 0.7,
        background: "rgba(0, 0, 0, 0.05)",
      },

      "& img": {
        width: "18px",
        height: "18px",
        display: "block",
      },
    },

    "& .card-name": {
      fontSize: "10px",
      fontWeight: "400",
      marginBottom: "6px",
      textTransform: "uppercase",
    },

    "& .position-input": {
      width: "100%",
      height: "28px",
      padding: "2px 10px",
      fontSize: "11px",
      borderRadius: "8px",
      marginBottom: "4px",
      border: "1px solid #ddd",
      "&:focus": {
        outline: "none",
        borderColor: "#000",
      },
    },

    "& .position-display": {
      fontSize: "10px",
      color: "rgb(0, 0, 0, 0.5)",
      letterSpacing: "0.01em",
      cursor: "pointer",
      padding: "4px 10px",
      marginTop: "-6px",
      borderRadius: "20px",
      transition: "background 0.2s ease",

      "&:hover": {
        color: "rgba(0, 0, 0, .3)",
      },
    },
  },

  "& .search-container": {
    position: "relative",
    width: "100%",
    marginBottom: "12px",
  },

  "& .card-search": {
    width: "100%",
    padding: "12px",
    paddingRight: "30px",
    border: "1px solid #ddd",
    borderRadius: "12px",
    fontSize: "12px",
    fontFamily: "inherit",

    "@mobile": {
      fontSize: "13px",
    },

    "&:focus": {
      outline: "none",
      borderColor: "#000",
    },
  },

  "& .clear-icon": {
    position: "absolute",
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    width: "16px",
    height: "16px",
    cursor: "pointer",
    display: "none",
    alignItems: "center",
    justifyContent: "center",
    backgroundImage: "url(/images/exit.svg)",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    opacity: 0.6,
    transition: "opacity 0.2s",

    "&.visible": {
      display: "flex",
    },

    "&:hover": {
      opacity: 0.7,
    },
  },

  "& .loading-cards": {
    display: "flex",
    justifyContent: "center",
    padding: "20px",
  },

  "& .card-picker": {
    width: "100%",
  },

  "& .category-cards": {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(70px, 1fr))",
    gap: "8px",
    opacity: 0,
    transition: "opacity 0.25s ease",
    minHeight: "300px",
    alignContent: "start",

    "&.loaded": {
      opacity: 1,
    },
  },

  "& .card-option": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "10px",
    border: "1px solid rgba(0, 0, 0, 0.15)",
    borderRadius: "8px",
    backgroundColor: "#fff",
    cursor: "pointer",
    transition: "all 0.2s",

    "&:hover:not(:disabled)": {
      border: "1px solid rgba(0, 0, 0, 0.15)",
      backgroundColor: "#ebebeb",
    },

    "&:disabled": {
      opacity: 0.4,
      cursor: "not-allowed",
    },

    "& img": {
      width: "40px",
      height: "auto",
      borderRadius: "3px",
      marginBottom: "4px",
    },

    "& span": {
      fontSize: "9px",
      textAlign: "center",
      lineHeight: 1.2,
    },
  },

  "& .suit-buttons": {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    justifyContent: "center",
    margin: "4px 0px 18px 0px",
  },



  "& .suit-button": {
    padding: "0px 9px",
    height: "26px",
    fontSize: "10px",
    fontWeight: "200",
    color: "rgba(0, 0, 0, 0.8)",
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    border: "1px solid rgba(0, 0, 0, 0.05)",
    borderRadius: "16px",
    backgroundColor: "#e6e6e6",
    cursor: "pointer",
    transition: "all 0.2s",

    "@notmobile": {
      width: "70px",
    },

    "&:hover": {
      backgroundColor: "#dadada",
    },

    "&.active": {
      backgroundColor: "#cfcfcf",
      color: "#000",
      border: "1px solid rgba(0, 0, 0, 0.25)",
    },


  },

  "& .import-journal-button": {
    position: "relative",
    zIndex: 5,
    padding: "8px 14px",
    fontSize: "12px",
    color: "rgba(0, 0, 0, 0.4)",
    backgroundColor: "transparent",
    border: "1px dashed rgba(0, 0, 0, 0.2)",
    borderRadius: "20px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    marginBottom: "20px",

    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.4)",
      color: "rgba(0, 0, 0, 0.5)",
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },

  "& .journal-picker": {
    width: "100%",
    marginBottom: "20px",
    minHeight: vh(50),
  },

  "& .journal-entries-list": {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  "& .journal-entry-option": {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "14px",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: "12px",
    backgroundColor: "#fff",
    cursor: "pointer",
    transition: "all 0.15s ease",
    textAlign: "left",
    width: "100%",

    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.25)",
      backgroundColor: "#fafafa",
    },
  },

  "& .journal-entry-cards": {
    display: "flex",
    gap: "6px",
    alignItems: "center",

    "& img": {
      width: "32px",
      height: "auto",
      borderRadius: "3px",
      border: "1px solid #eee",
    },
  },

  "& .journal-entry-more": {
    fontSize: "10px",
    color: "#999",
    marginLeft: "2px",
  },

  "& .journal-entry-info": {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  "& .journal-entry-query": {
    fontSize: "12px",
    color: "#000",
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  "& .journal-entry-date": {
    fontSize: "10px",
    color: "#999",
  },

  "& .overlay-footer": {
    position: "sticky",
    bottom: "-32px",
    left: "-32px",
    right: "-32px",
    width: "calc(100% + 64px)",
    minHeight: "100px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    borderTop: "1px solid $gray400",
    backgroundColor: "$wash",
    padding: "12px",
    marginTop: "auto",
    marginBottom: "-32px",
  },

  [`.${darkTheme.className} &`]: {
    backgroundColor: "$darkwash",
    color: "$text",
    "& .x-button, & .back-button": { filter: "invert(1)" },
    "& .import-journal-button": { color: "rgba(255,255,255,0.5)", borderColor: "rgba(255,255,255,0.3)", "&:hover": { color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.5)", backgroundColor: "rgba(255,255,255,0.06)" } },
    "& .form-section label": { color: "$text" },
    "& .form-section textarea, & .card-search, & .selected-card .position-input": { backgroundColor: "#000", color: "$text", borderColor: "#505050", "&:focus": { borderColor: "#aaa" } },
    "& .selected-card .card-preview img": { borderColor: "#505050" },
    "& .selected-card .card-preview .remove-card": { backgroundColor: "#fff", color: "#000" },
    "& .selected-card .position-display": { color: "rgba(255,255,255,0.6)", "&:hover": { color: "rgba(255,255,255,0.4)" } },
    "& .selected-card .reverse-button img, & .clear-icon": { filter: "invert(1)" },
    "& .selected-card .reverse-button:hover": { background: "rgba(255,255,255,0.1)" },
    "& .card-option": { backgroundColor: "#000", borderColor: "rgba(255,255,255,0.25)", color: "$text", "&:hover:not(:disabled)": { backgroundColor: "#262626", borderColor: "rgba(255,255,255,0.25)" } },
    "& .suit-button": { backgroundColor: "#383838", color: "rgba(255,255,255,0.8)", borderColor: "rgba(255,255,255,0.05)", "&:hover": { backgroundColor: "#4a4a4a" }, "&.active": { backgroundColor: "#fff", color: "#000" } },
    "& .journal-entry-option": { backgroundColor: "#000", borderColor: "rgba(255,255,255,0.25)", color: "$text" },
    "& .journal-entry-option:hover": { backgroundColor: "#262626", borderColor: "rgba(255,255,255,0.4)" },
    "& .journal-entry-cards img": { borderColor: "#505050" },
    "& .journal-entry-query": { color: "$text" },
    "& .journal-entry-more, & .journal-entry-date": { color: "rgba(255,255,255,0.5)" },
    "& .overlay-footer": { backgroundColor: "$darkwash", borderTopColor: "$gray600" },
  },

  variants: {
    isIOSNative: {
      true: {
          "& .overlay-footer": {
            height: "auto",
            paddingBottom: "210px",
        },
      },
    },
  },
});
